import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

/**
 * Shared AI Explanation Renderer
 *
 * Used by both QuestionViewer and QuestionList to ensure
 * identical rendering of AI-generated explanations.
 *
 * Props:
 *   content  - string containing markdown + LaTeX
 *   style    - optional container style overrides
 */
export default function AIExplanationRenderer({ content, style }) {
  if (!content) return null;

  return (
    <div className="ai-explanation-content" style={style}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          h2: ({ children }) => {
            const text = String(children);
            const sections = {
              '📘': { bg: '#eff6ff', border: '#3b82f6', color: '#1e40af' },
              '📐': { bg: '#faf5ff', border: '#a855f7', color: '#7e22ce' },
              '✅': { bg: '#f0fdf4', border: '#22c55e', color: '#166534' },
              '🎯': { bg: '#ecfdf5', border: '#10b981', color: '#065f46' },
              '💡': { bg: '#fffbeb', border: '#f59e0b', color: '#92400e' },
            };
            const match = Object.keys(sections).find(k => text.includes(k));
            if (match) {
              const s = sections[match];
              return (
                <div style={{
                  background: s.bg,
                  borderLeft: `4px solid ${s.border}`,
                  borderRadius: '0 10px 10px 0',
                  padding: '12px 16px',
                  marginTop: '20px',
                  marginBottom: '4px',
                }}>
                  <h3 style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: s.color,
                    margin: 0,
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}>{text}</h3>
                </div>
              );
            }
            return (
              <h3 style={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#1e293b',
                marginTop: '20px',
                marginBottom: '4px',
                fontFamily: "'Space Grotesk', sans-serif",
              }}>{children}</h3>
            );
          },
          h3: ({ children }) => (
            <h4 style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#334155',
              marginTop: '16px',
              marginBottom: '4px',
              fontFamily: "'Space Grotesk', sans-serif",
            }}>{children}</h4>
          ),
          p: ({ children }) => (
            <p style={{
              marginBottom: '12px',
              lineHeight: '1.75',
              color: '#374151',
              fontSize: '14px',
            }}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul style={{
              listStyle: 'disc',
              paddingLeft: '20px',
              marginBottom: '12px',
              color: '#374151',
              fontSize: '14px',
            }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{
              listStyle: 'decimal',
              paddingLeft: '20px',
              marginBottom: '12px',
              color: '#374151',
              fontSize: '14px',
            }}>{children}</ol>
          ),
          li: ({ children }) => (
            <li style={{ marginBottom: '4px', lineHeight: '1.65' }}>{children}</li>
          ),
          strong: ({ children }) => (
            <strong style={{ color: '#1e293b', fontWeight: 700 }}>{children}</strong>
          ),
          em: ({ children }) => (
            <em style={{ color: '#64748b' }}>{children}</em>
          ),
          blockquote: ({ children }) => {
            const text = String(children);
            if (text.includes('💡')) {
              return (
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  margin: '16px 0',
                  fontSize: '13px',
                  color: '#92400e',
                  lineHeight: '1.6',
                }}>{children}</div>
              );
            }
            return (
              <blockquote style={{
                borderLeft: '4px solid #6366f1',
                margin: '16px 0',
                color: '#475569',
                background: '#f1f5f9',
                borderRadius: '0 10px 10px 0',
                padding: '12px 16px',
                fontSize: '13px',
              }}>{children}</blockquote>
            );
          },
          code: ({ inline, className, children, ...props }) => {
            if (inline) {
              return (
                <code style={{
                  background: '#f1f5f9',
                  color: '#6366f1',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                }} {...props}>{children}</code>
              );
            }
            return (
              <div style={{
                margin: '12px 0',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{
                  background: '#f1f5f9',
                  padding: '6px 12px',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: '11px',
                  color: '#94a3b8',
                  fontFamily: 'monospace',
                }}>code</div>
                <pre style={{
                  margin: 0,
                  padding: '14px',
                  background: '#f8fafc',
                  overflowX: 'auto',
                }}>
                  <code style={{
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    color: '#334155',
                    lineHeight: '1.5',
                  }} {...props}>{children}</code>
                </pre>
              </div>
            );
          },
          table: ({ children }) => (
            <div style={{
              margin: '14px 0',
              overflowX: 'auto',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
            }}>
              <table style={{
                width: '100%',
                fontSize: '13px',
                borderCollapse: 'collapse',
              }}>{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead style={{ background: '#f1f5f9' }}>{children}</thead>
          ),
          th: ({ children }) => (
            <th style={{
              padding: '10px 14px',
              textAlign: 'left',
              fontWeight: 600,
              color: '#1e293b',
              borderBottom: '1px solid #e2e8f0',
              fontSize: '12px',
            }}>{children}</th>
          ),
          td: ({ children }) => (
            <td style={{
              padding: '10px 14px',
              color: '#475569',
              borderBottom: '1px solid #f1f5f9',
            }}>{children}</td>
          ),
          hr: () => (
            <hr style={{
              margin: '16px 0',
              border: 'none',
              borderTop: '1px solid #e2e8f0',
            }} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
